import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  mergeResults,
  extendArchitecture,
} from '#lit/components/mixins/immutable_component/index.js';
import {html, nothing} from 'lit';
import {when} from 'lit/directives/when.js';
import type {Nullable} from '#lit/types/general.js';
import type {VariantsFor, SubModelFor} from '#lit/types/architecture.js';
import type {CursorEvent} from '#lit/lib/cursor.js';
import type {BoundingBox} from '#lit/lib/bezier.js';
import {Bezier, BezierStyle} from '#lit/lib/bezier.js';
import {execWhenMoved, execOnLeave} from './utils.js';
import type CanvasNode from '../canvas_node/index.js';
import CanvasGroup from '../canvas_group/index.js';
import type CanvasConnection from './index.js';
import * as Super from '../canvas_element/architecture.js';
import {isCanvasNodeType} from '#lit/types/canvas.js';
import {getNodeFromPath} from '../canvas_node/utils.js';

export type CanvasTarget = CanvasNode | CanvasGroup;

const Curve = Bezier.create();

export enum CanvasConnectionEventAction {
  Connected = 'connected',
  Disconnected = 'disconnected',
}

export type Model = {
  connected: boolean;
  connectorIndex: number;

  source: Nullable<CanvasNode>;
  target: Nullable<CanvasTarget>;

  nodeKeys: {
    source: Nullable<string>;
    target: Nullable<string>;
  };

  bezier: {
    startBound: BoundingBox;
    endBound: Nullable<BoundingBox>;
  };
} & Super.Model;

type SubModel = SubModelFor<Model, Super.Model>;

export const Msg = makeVariants({
  setConnected: Variant<boolean>()('set-connected'),
  setNodeKeys: Variant<Partial<Model['nodeKeys']>>()('set-node-keys'),
  setSource: Variant<CanvasNode>()('set-source'),
  setTarget: Variant<CanvasTarget>()('set-target'),
  setStartBound: Variant<BoundingBox>()('set-start-bound'),
  setEndBound: Variant<BoundingBox>()('set-end-bound'),
  resetTarget: Variant<void>()('reset-target'),
  ...Super.Msg,
});

export type Msg = VariantsFor<typeof Msg>;

const SuperProgram = extendArchitecture<Super.Model, Super.Msg, Model, Msg>(
  Super.main
);

// -- Init

function init(
  this: CanvasConnection,
  props: InitialProperties,
  cmd: Cmd,
  model?: Model
): ImmutableResult<Model> {
  const source = <CanvasConnection['source']>props.get('source') ?? null;
  const target = <CanvasConnection['target']>props.get('target') ?? null;
  const connectorIndex =
    <number>props.get('connectorIndex') !== -1
      ? <number>props.get('connectorIndex')
      : (model?.connectorIndex ?? -1);

  if (connectorIndex < 0) console.error('Invalid connector index.');

  const result: SubModel = {
    ...model,
    // eslint-disable-next-line no-constant-binary-expression
    connected: model?.connected ?? !!(source && target) ?? false,
    connectorIndex,
    source,
    target,
    bezier: {
      startBound:
        source?.connectorBoundingBox({index: connectorIndex}) ?? new DOMRect(),
      endBound: target?.boundingBox ?? null,
    },
    nodeKeys: {
      source:
        model?.nodeKeys.source ??
        <CanvasConnection['sourceKey']>props.get('sourceKey'),
      target:
        model?.nodeKeys.target ??
        <CanvasConnection['targetKey']>props.get('targetKey'),
    },
  };

  return mergeResults(result, SuperProgram.init(props, cmd));
}

// -- Update

function createDrawHandlers(host: CanvasConnection) {
  return {
    /**
     * While drawing, we want to rerender on mouse move to simulate drawing.
     */
    onMove() {
      if (host.model.connected) return {destroy: true};
      host.rerender();

      if (host.model.target) host.model.target.setSocketPosition();
    },

    /**
     * Attempts to finalize the connection with any target currently beneath
     * the mouse cursor.
     */
    onClick(event: CursorEvent) {
      if (host.model.connected) return {destroy: true};

      if (host.$canvas?.isInPath(event.target, event.composedPath ?? [])) {
        host.delete();
        return {destroy: true};
      }

      const target =
        CanvasGroup.getGroupFromPath(event.composedPath ?? []) ??
        getNodeFromPath(event.composedPath ?? []);

      if (!target || target.isSameNode(host.model.source)) return;

      if (host.model.source?.walker.hasPrevious(target)) {
        host.delete();
        return {destroy: true};
      }

      target.unfocus();

      try {
        target.connect(host);
      } catch {
        host.delete();
        return {destroy: true};
      }

      host.handleUpdate(Msg.setTarget(target));
      host.handleUpdate(Msg.setConnected(true));

      execWhenMoved(target, () => {
        host.handleUpdate(Msg.setEndBound(target.boundingBox));
      });

      return {destroy: true};
    },

    /**
     * Determines if a potential target is under the cursor while drawing.
     * If there is one, we freeze the Y-axis of the connection being to the
     * top of the potential target.
     */
    findTarget(event: CursorEvent) {
      if (host.model.connected || !host.isConnected) return {destroy: true};
      if (host.model.target) return;

      const source = host.model.source;
      const element =
        CanvasGroup.getGroupFromPath(event.composedPath ?? []) ??
        getNodeFromPath(event.composedPath ?? []);
      const target =
        isCanvasNodeType(element) && element.hasGroup()
          ? element.$group!
          : element;

      if (
        target &&
        source &&
        !target.isSameNode(source) &&
        (!source?.$group || !target.isSameNode(source?.$group)) &&
        !source.walker.hasPrevious(target)
      ) {
        target.focus();

        host.handleUpdate(Msg.setTarget(target));
        host.handleUpdate(Msg.setEndBound(target.boundingBox));

        execOnLeave(
          target.root()!,
          () => {
            host.handleUpdate(Msg.resetTarget());
            target.unfocus();

            return {destroy: true};
          },
          {immediate: false}
        );
      }
    },
  };
}

function whenAttached(host: CanvasConnection) {
  const {model, $canvas} = host;

  if ($canvas) Curve.attachCursor($canvas?.cursor);

  // If there's already a source, we should attach the connection curve
  // to its bounding box.
  if (model.source) {
    execWhenMoved(model.source, () => {
      if (!isCanvasNodeType(model.source)) {
        console.error(
          'Attempted to use an invalid source object.',
          model.source
        );
        return;
      }

      const boundingBox = model.source!.connectorBoundingBox({
        index: model.connectorIndex,
      });

      if (boundingBox) host.handleUpdate(Msg.setStartBound(boundingBox));
    });
  }

  // Source or target keys were provided instead:
  // 1) find the nodes, and 2) attach the connection to their bounding boxes.
  for (const type in model.nodeKeys) {
    const nodeKey = model.nodeKeys[type as keyof typeof model.nodeKeys];
    if (!nodeKey) continue;

    const node = host.$canvas?.model.nodeRegistry.get(nodeKey);
    if (!node || !isCanvasNodeType(node)) return;

    switch (type) {
      case 'source':
        host.handleUpdate(Msg.setSource(node));
        execWhenMoved(node, () => {
          host.handleUpdate(Msg.setStartBound(node.boundingBox));
        });

        break;
      case 'target':
        host.handleUpdate(Msg.setTarget(node));
        execWhenMoved(node, () => {
          host.handleUpdate(Msg.setEndBound(node.boundingBox));
        });

        break;
    }
  }

  // If no target was provided, we can be sure we're drawing at this point.
  if (!model.target) {
    const handlers = createDrawHandlers(host);

    host.$canvas?.cursor
      .on('drag', handlers.onMove.bind(handlers))
      .on('move', handlers.onMove.bind(handlers))
      .on('move', handlers.findTarget.bind(handlers))
      .on('click', handlers.onClick.bind(handlers));

    host.$canvas?.addEventListener('canvas-updated', () => host.rerender(), {
      signal: host.disconnectSignal,
    });
  }
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-connected':
      if (model.connected === msg.value) return model;
      return [
        {...model, connected: msg.value},
        cmd((host: CanvasConnection) => {
          const event = msg.value
            ? host.createEvent(CanvasConnectionEventAction.Connected)
            : host.createEvent(CanvasConnectionEventAction.Disconnected);
          host.dispatchEvent(event);
        }),
      ] as const;

    case 'set-node-keys':
      return {
        ...model,
        nodeKeys: {...model.nodeKeys, ...msg.value},
      };

    case 'set-source':
      return {
        ...model,
        source: msg.value,
      };

    case 'set-target':
      return {
        ...model,
        target: msg.value,
      };

    case 'reset-target':
      return {
        ...model,
        target: null,
      };

    case 'set-start-bound':
      return {
        ...model,
        bezier: {
          ...model.bezier,
          startBound: msg.value,
        },
      };

    case 'set-end-bound':
      return {
        ...model,
        bezier: {
          ...model.bezier,
          endBound: msg.value,
        },
      };

    case 'set-attached':
      return mergeResults(
        [model, cmd(whenAttached)],
        SuperProgram.update(msg, model, cmd)
      );
  }

  return SuperProgram.update(msg, model, cmd);
}

// -- View

export function view(this: CanvasConnection, model: Model) {
  if (!model.attached) return html`${nothing}`;

  return html`
    <div class="canvas-connection">
      ${when(
        model.bezier.startBound && model.bezier.endBound && model.connected,
        () =>
          Curve.draw(model.bezier.startBound, model.bezier.endBound!, {
            style: BezierStyle.SOLID,
          }),
        () => {
          const [mouseX, mouseY] =
            this.$canvas?.cursor.globalPosition.asTuple() ?? [0, 0];
          const planeRect = this.$canvas?.model.planeRect ?? {x: 0, y: 0};
          const scale = this.$canvas?.model.scale ?? 1;

          const x = (mouseX - planeRect.x) / scale;
          const y = (mouseY - planeRect.y) / scale;

          return Curve.drawWithCursor(
            model.bezier.startBound,
            {x, y},
            {
              style: BezierStyle.DASHED,
              xConstraint:
                this.$canvas && model.target
                  ? model.target.socketOffset().x
                  : null,
              yConstraint: model.target?.root()
                ? model.target.socketOffset().y
                : null,
            }
          );
        }
      )}
    </div>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
